// firefights.ts
import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";

// FirefightsPopup.tsx
import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  buildRallySegmentUrl,
  VideoOverlayControls,
} from "./GameStory_FastShotsPopup";
import { ShotNumberOverlayLayer } from "./RallyPopup";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

export function computeFirefightsForAllRows(args: {
  rows: KitchenArrivalRow[];
  thresholdMs: number; // 600
  fireFightMinShots?: number; // 3
  getTeamFromPlayerId: (playerId: number) => 0 | 1;
  preMs?: number;
  postMs?: number;
}): FirefightClip[] {
  const { rows, thresholdMs, getTeamFromPlayerId } = args;
  const out: FirefightClip[] = [];

  for (let rallyIndex = 0; rallyIndex < rows.length; rallyIndex++) {
    const rally = rows[rallyIndex];
    if (!rally) continue;

    const links = computeFirefightLinksForRally({
      rally,
      rallyIndex,
      thresholdMs,
      getTeamFromPlayerId,
    });

    const seqs = groupLinksIntoFirefights({
      links,
      fireFightMinShots: args.fireFightMinShots ?? 3,
    });

    for (const seq of seqs) {
      out.push(
        buildFirefightClip({
          seq,
          preMs: args.preMs ?? 900,
          postMs: args.postMs ?? 700,
          // optional if you have rally bounds handy:
          rallyStartMs: rally.segmentStartMs,
          rallyEndMs: rally.segmentEndMs,
        }),
      );
    }
  }

  return out;
}

function shotIsBad(overlay: ShotNumberOverlay): boolean {
  const s = overlay?.rawShot;

  if (s?.resulting_ball_movement?.trajectory?.end?.zone === "out") return true;

  const faults = s?.errors?.faults;
  if (faults?.net === true) return true;

  const faultStr = JSON.stringify(faults ?? {});
  if (faultStr.includes("net") || faultStr.includes("tape")) return true;

  return false;
}

/**
 * This is basically your FastReactionEvent but we keep some extra
 * info to group into sequences.
 *
 * NOTE: Here "reactionMs" is measured as curStart - prevStart (same as your current code).
 */
export type FirefightLink = {
  rallyIndex: number;

  // link is between prev shot (i-1) and current shot (i)
  prevShotIndex: number; // i-1
  curShotIndex: number; // i

  // 1-based shot numbers for UI/overlay highlight convenience
  prevShotNumber: number;
  curShotNumber: number;

  team: 0 | 1;
  playerId: number;

  reactionMs: number;
  thresholdMs: number;

  prevShotStartMs: number;
  prevShotEndMs: number;
  curShotStartMs: number;
  curShotEndMs: number;

  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;
};

export type FirefightSequence = {
  key: string;
  rallyIndex: number;

  thresholdMs: number;

  // The run covers shots [startShotIndex..endShotIndex] inclusive
  startShotIndex: number;
  endShotIndex: number;

  // 1-based
  startShotNumber: number;
  endShotNumber: number;

  // aggregate stats
  linkCount: number; // number of fast links in the run
  shotCount: number; // linkCount + 1
  minReactionMs: number;
  maxReactionMs: number;
  avgReactionMs: number;

  // for display
  teamsInvolved: Array<0 | 1>;
  playerIdsInvolved: number[];

  // raw links
  links: FirefightLink[];

  // timing bounds for clip building
  seqStartMs: number; // start of first shot
  seqEndMs: number; // end of last shot
};

export type FirefightClip = {
  key: string;
  seq: FirefightSequence;
  clipStartMs: number;
  clipEndMs: number;
};

export function computeFirefightLinksForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  thresholdMs: number;
  getTeamFromPlayerId: (playerId: number) => 0 | 1;
}): FirefightLink[] {
  const { rally, rallyIndex, thresholdMs, getTeamFromPlayerId } = args;

  const shots = [
    ...(Array.isArray(rally?.shotNumberOverlays)
      ? rally.shotNumberOverlays
      : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: FirefightLink[] = [];

  for (let i = 1; i < shots.length; i++) {
    const prev = shots[i - 1];
    const cur = shots[i];

    const prevStart = prev?.rawShot?.start_ms ?? undefined;
    const prevEnd = prev?.rawShot?.end_ms ?? prev?.rawShot?.start_ms;
    const curStart = cur?.rawShot?.start_ms;
    const curEnd = cur?.rawShot?.end_ms ?? cur?.rawShot?.start_ms;

    if (
      typeof prevStart !== "number" ||
      typeof prevEnd !== "number" ||
      typeof curStart !== "number" ||
      typeof curEnd !== "number"
    )
      continue;

    const reactionMs = curStart - prevStart; // matches your current approach
    if (reactionMs < 0) continue;
    if (reactionMs > thresholdMs) continue;
    if (shotIsBad(cur)) continue;

    const playerId = Number(cur.playerId);

    out.push({
      rallyIndex,
      prevShotIndex: i - 1,
      curShotIndex: i,
      prevShotNumber: i, // 1-based
      curShotNumber: i + 1,
      team: getTeamFromPlayerId(playerId),
      playerId,
      reactionMs,
      thresholdMs,
      prevShotStartMs: prevStart,
      prevShotEndMs: prevEnd,
      curShotStartMs: curStart,
      curShotEndMs: curEnd,
      isVolley: !!cur.rawShot?.is_volley,
      strokeType: cur.rawShot?.stroke_type,
      shotType: cur.rawShot?.shot_type,
    });
  }

  return out;
}

/**
 * Group consecutive fast links into sequences.
 *
 * A sequence is a run where each link is consecutive in shot index:
 *   link.curShotIndex of previous == link.prevShotIndex of next
 *
 * fireFightMinShots = 3 means we need >=2 links.
 */
export function groupLinksIntoFirefights(args: {
  links: FirefightLink[];
  fireFightMinShots?: number; // default 3
}): FirefightSequence[] {
  const minShots = args.fireFightMinShots ?? 3;
  const minLinks = Math.max(1, minShots - 1);

  if (!args.links.length) return [];

  const links = [...args.links].sort((a, b) => a.curShotIndex - b.curShotIndex);

  const out: FirefightSequence[] = [];

  let curRun: FirefightLink[] = [];

  const flush = () => {
    if (curRun.length < minLinks) {
      curRun = [];
      return;
    }

    const first = curRun[0];
    const last = curRun[curRun.length - 1];

    const startShotIndex = first.prevShotIndex;
    const endShotIndex = last.curShotIndex;

    const reactions = curRun.map((l) => l.reactionMs);
    const minReactionMs = Math.min(...reactions);
    const maxReactionMs = Math.max(...reactions);
    const avgReactionMs =
      reactions.reduce((a, b) => a + b, 0) / reactions.length;

    const teamSet = new Set<0 | 1>();
    const pidSet = new Set<number>();
    for (const l of curRun) {
      teamSet.add(l.team);
      pidSet.add(l.playerId);
    }

    const seqStartMs = first.prevShotStartMs;
    const seqEndMs = last.curShotEndMs;

    const seq: FirefightSequence = {
      key: `r${first.rallyIndex}_ff_${startShotIndex}_${endShotIndex}_t${first.thresholdMs}`,
      rallyIndex: first.rallyIndex,
      thresholdMs: first.thresholdMs,

      startShotIndex,
      endShotIndex,
      startShotNumber: startShotIndex + 1,
      endShotNumber: endShotIndex + 1,

      linkCount: curRun.length,
      shotCount: curRun.length + 1,

      minReactionMs,
      maxReactionMs,
      avgReactionMs,

      teamsInvolved: Array.from(teamSet.values()).sort((a, b) => a - b),
      playerIdsInvolved: Array.from(pidSet.values()).sort((a, b) => a - b),

      links: curRun,
      seqStartMs,
      seqEndMs,
    };

    out.push(seq);
    curRun = [];
  };

  for (const l of links) {
    if (!curRun.length) {
      curRun.push(l);
      continue;
    }

    const prev = curRun[curRun.length - 1];

    // consecutive chain: prev.curShotIndex == l.prevShotIndex
    if (prev.curShotIndex === l.prevShotIndex) {
      curRun.push(l);
    } else {
      flush();
      curRun.push(l);
    }
  }
  flush();

  return out;
}

export function buildFirefightClip(args: {
  seq: FirefightSequence;
  preMs?: number; // default 900
  postMs?: number; // default 700
  rallyStartMs?: number;
  rallyEndMs?: number;
}): FirefightClip {
  const preMs = args.preMs ?? 900;
  const postMs = args.postMs ?? 700;

  let clipStartMs = args.seq.seqStartMs - preMs;
  let clipEndMs = args.seq.seqEndMs + postMs;

  if (typeof args.rallyStartMs === "number")
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  if (typeof args.rallyEndMs === "number")
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);

  if (clipEndMs - clipStartMs < 400) clipEndMs = clipStartMs + 400;

  return {
    key: `${args.seq.key}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    seq: args.seq,
    clipStartMs,
    clipEndMs,
  };
}

type SortKey = "rally" | "shots" | "avg" | "min";
type SortDir = "asc" | "desc";

export function FirefightsPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null; // unused (kept for parity)
  onClose: () => void;

  packageId: string;

  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  clipsAll: FirefightClip[]; // computed outside (recommended)
  clipsThisRally?: FirefightClip[]; // optional if you want mode toggles later
  firefightRallySet: Set<number>; // for conditional overlay in timeline

  playerLabel?: (playerId: number) => string;
  teamLabel?: (team: 0 | 1) => string;
  renderPlayerIdentity: RenderPlayerIdentity;

  teamColor?: (team: 0 | 1, alpha?: number) => string;
  teamName?: (team: 0 | 1) => string;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    clipsAll,
    playerLabel,
    teamLabel,
    teamColor,
    teamName,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  const clips = clipsAll;

  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(clips[0]?.key ?? null);
  }, [open, clipsAll.length]);

  const selected = clips.find((c) => c.key === selectedKey) ?? clips[0] ?? null;

  // floating position (same pattern)
  const [pos, setPos] = React.useState({ x: 18, y: 50 });
  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const nextX = Math.max(
        0,
        Math.min(window.innerWidth - 220, d.startPosX + dx),
      );
      const nextY = Math.max(
        0,
        Math.min(window.innerHeight - 140, d.startPosY + dy),
      );
      setPos({ x: nextX, y: nextY });
    };
    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  // sorting
  const [sortKey, setSortKey] = React.useState<SortKey>("avg");
  const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const sortedClips = React.useMemo(() => {
    const arr = [...clips];
    arr.sort((a, b) => {
      const sa = a.seq;
      const sb = b.seq;

      const av =
        sortKey === "rally"
          ? sa.rallyIndex
          : sortKey === "shots"
            ? sa.shotCount
            : sortKey === "min"
              ? sa.minReactionMs
              : sa.avgReactionMs;

      const bv =
        sortKey === "rally"
          ? sb.rallyIndex
          : sortKey === "shots"
            ? sb.shotCount
            : sortKey === "min"
              ? sb.minReactionMs
              : sb.avgReactionMs;

      const d = av - bv;
      return sortDir === "asc" ? d : -d;
    });
    return arr;
  }, [clips, sortKey, sortDir]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(sortedClips[0]?.key ?? null);
  }, [open, sortKey, sortDir, sortedClips.length]);

  // --- dual video like your FastReactions popup ---
  const clipVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const rallyVideoRef = React.useRef<HTMLVideoElement | null>(null);

  const [clipRate, setClipRate] = React.useState<0.25 | 0.5 | 1>(1);
  const [rallyRate, setRallyRate] = React.useState<0.25 | 0.5 | 1>(1);

  const [clipPaused, setClipPaused] = React.useState(false);
  const [rallyPaused, setRallyPaused] = React.useState(false);

  const clipShouldMute = clipRate < 1;
  const rallyShouldMute = rallyRate < 1;

  // const clipUrl = React.useMemo(() => {
  //   if (!selected) return null;
  //   return buildRallySegmentUrl({
  //     packageId,
  //     rallyIndex: selected.seq.rallyIndex,
  //     startMs: Math.round(selected.clipStartMs),
  //     endMs: Math.round(selected.clipEndMs),
  //     download: 0,
  //   });
  // }, [packageId, selected?.key]);

  // React.useEffect(() => {
  //   const v = clipVideoRef.current;
  //   if (!v) return;
  //   v.muted = clipShouldMute;
  //   v.defaultMuted = clipShouldMute;
  // }, [clipShouldMute, clipUrl]);

  // const selectedRow = React.useMemo(() => {
  //   if (!rows || !selected) return null;
  //   return rows[selected.seq.rallyIndex] ?? null;
  // }, [rows, selected?.key]);

  // const rallyVideoUrl = selectedRow?.videoUrl ?? null;

  // React.useEffect(() => {
  //   const v = rallyVideoRef.current;
  //   if (!v) return;
  //   v.muted = rallyShouldMute;
  //   v.defaultMuted = rallyShouldMute;
  // }, [rallyShouldMute, rallyVideoUrl]);

  // React.useEffect(() => setClipPaused(false), [clipUrl]);
  // React.useEffect(() => setRallyPaused(false), [rallyVideoUrl]);

  const clipUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.seq.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0,
    });
  }, [packageId, selected?.key]);

  const selectedRow = React.useMemo(() => {
    if (!rows || !selected) return null;
    return rows[selected.seq.rallyIndex] ?? null;
  }, [rows, selected?.key]);

  const rallyVideoUrl = selectedRow?.videoUrl ?? null;

  const clipRow = React.useMemo(() => {
    if (!selected || !selectedRow) return null;
    return selectedRow;
  }, [selectedRow, selected?.key]);

  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    // bump nonce anytime the selected clip changes
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    // selected? event?.rallyIndex,
    // selected?.event?.lobShotIndex,
    selected?.key,
    selected?.seq.rallyIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  const selectedLoopRegion = React.useMemo(() => {
    if (!selectedRow || !selected) return undefined;
    const segmentStartMs = selectedRow.segmentStartMs ?? selectedRow.startMs ?? 0;
    return {
      enabled: true,
      aSec: Math.max(0, (selected.clipStartMs - segmentStartMs) / 1000),
      bSec: Math.max(0.5, (selected.clipEndMs - segmentStartMs) / 1000),
      nonce: seedNonce,
    };
  }, [selectedRow, selected?.key, seedNonce]);

  if (!open) return null;

  const headerSummary = selected
    ? `Rally ${selected.seq.rallyIndex + 1} • shots ${selected.seq.startShotNumber}–${selected.seq.endShotNumber} • ${selected.seq.shotCount} shots`
    : "No firefights";

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1320,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",

        // border: "3px solid yellow !important",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Typography sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
            🔥 Firefights
          </Typography>
          <Typography
            sx={{ opacity: 0.75, fontSize: 12, whiteSpace: "nowrap" }}
          >
            {clips.length} sequences
          </Typography>
          <Typography
            sx={{ opacity: 0.75, fontSize: 12, whiteSpace: "nowrap" }}
          >
            {headerSummary}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={props.onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
        >
          Close
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left list */}
        <Box
          sx={{
            width: 440,
            maxWidth: "55%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
            "& th, & td": { fontSize: "11px", padding: "6px !important" },
            "& th": { verticalAlign: "bottom" },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSort("rally")}
                >
                  Rally{" "}
                  {sortKey === "rally" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSort("shots")}
                >
                  Shots{" "}
                  {sortKey === "shots" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  align="right"
                  onClick={() => toggleSort("avg")}
                >
                  Avg Reaction (ms)
                  {sortKey === "avg" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  align="right"
                  onClick={() => toggleSort("min")}
                >
                  Min Reaction (ms){" "}
                  {sortKey === "min" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedClips.map((c) => {
                const s = c.seq;
                const isSel = c.key === selectedKey;

                return (
                  <TableRow
                    key={c.key}
                    hover
                    onClick={() => setSelectedKey(c.key)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSel ? "rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {s.rallyIndex + 1}
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.35,
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 900,
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s.startShotNumber}–{s.endShotNumber}{" "}
                          <Box
                            component="span"
                            sx={{ opacity: 0.7, fontWeight: 700 }}
                          >
                            ({s.shotCount} shots)
                            {/* {s.linkCount} fast links */}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {Math.round(s.avgReactionMs)}
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {Math.round(s.minReactionMs)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Right videos */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selected ? (
              <Typography sx={{ fontWeight: 900 }}>
                Rally {selected.seq.rallyIndex + 1} • shots{" "}
                {selected.seq.startShotNumber}–{selected.seq.endShotNumber} •{" "}
                {selected.seq.shotCount} shots • avg{" "}
                {Math.round(selected.seq.avgReactionMs)}ms • min{" "}
                {Math.round(selected.seq.minReactionMs)}ms
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.75 }}>
                No firefights for this selection.
              </Typography>
            )}
          </Box>

          <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={40}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>

          {/* TOP: firefight clip */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "black",
            }}
          >
            {
              clipRow && selected ? (
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <ActiveRallyVideo
                    insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                    renderPlayerIdentity={renderPlayerIdentity}
                    aiContextLabel="Firefights"
                    focusedFilter={null}
                    packageId={packageId}
                    row={clipRow}
                    rows={rows}
                    activeShotNumber={selected.seq.startShotNumber}
                    showCondensedTimeline={false}
                    fontColor={"white"}
                    controlsPlacement={"left"}
                    firefightsClips={clipsAll}
                    firefightRallySet={firefightRallySet}
                    showRateControls={true}
                    showTitle={false}
                    showScoreEtc={false}
                    showAi={true}
                    aiDrawerMode={"split"}
                    showLoopScrubber={true}
                    defaultLoopUiOpen={false}
                    showPopoutButton={true}
                    isInFullPopup={false}
                    seedPlayback={{
                      timeSec: selectedLoopRegion?.aSec ?? 0,
                      paused: false,
                      nonce: seedNonce,
                    }}
                    seedLoopRegion={selectedLoopRegion}
                    seekToActiveShotOnChange={false}
                  />

                  {/* keep your existing label overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 5,
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: "rgba(0,0,0,0.65)",
                      color: "white",
                      fontSize: 12,
                      lineHeight: 1.25,
                      maxWidth: "75%",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: 900,
                        fontSize: 13,
                        display: "inline-block",
                        mr: 0.75,
                      }}
                    >
                      🔥 Firefight: {selected.seq.shotCount} shots (
                      {selected.seq.startShotNumber}–
                      {selected.seq.endShotNumber})
                    </Box>
                  </Box>
                </Box>
              ) : null
              // <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>No clip selected.</Box>
            }

            {
              clipUrl && selected && clipUrl === "blah" ? (
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <VideoOverlayControls
                    videoRef={clipVideoRef}
                    rate={clipRate}
                    setRate={setClipRate}
                    paused={clipPaused}
                    setPaused={setClipPaused}
                  />

                  <video
                    ref={clipVideoRef}
                    key={clipUrl}
                    src={clipUrl}
                    autoPlay
                    loop
                    playsInline
                    muted={clipShouldMute}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      if (v.paused) {
                        v.play().catch(() => {});
                        setClipPaused(false);
                      } else {
                        v.pause();
                        setClipPaused(true);
                      }
                    }}
                    onLoadedMetadata={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      v.defaultPlaybackRate = clipRate;
                      v.playbackRate = clipRate;
                    }}
                    onRateChange={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      if (Math.abs(v.playbackRate - clipRate) > 0.01) {
                        v.defaultPlaybackRate = clipRate;
                        v.playbackRate = clipRate;
                      }
                    }}
                  />

                  {selectedRow?.shotNumberOverlays && (
                    <ShotNumberOverlayLayer
                      focusedFilter={null}
                      videoRef={clipVideoRef}
                      overlays={selectedRow.shotNumberOverlays}
                      segmentStartMs={selected.clipStartMs}
                      highlightedShotNumber={selected.seq.startShotNumber}
                      highlightedExtraText={`${selected.seq.shotCount} shots`}
                      //reactionHighlightMs={selected.seq.thresholdMs}
                      showThresholdValuesInLabel={false}
                      firefightsAll={clipsAll}
                      rallyIndex={selectedRow.rallyIndex}
                    />
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 5,
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: "rgba(0,0,0,0.65)",
                      color: "white",
                      fontSize: 12,
                      lineHeight: 1.25,
                      maxWidth: "75%",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: 900,
                        fontSize: 13,
                        display: "inline-block",
                        mr: 0.75,
                      }}
                    >
                      🔥 Firefight: {selected.seq.shotCount} shots (
                      {selected.seq.startShotNumber}–
                      {selected.seq.endShotNumber})
                    </Box>
                    {/* <Box component="span" sx={{ opacity: 0.85, fontWeight: 700 }}>
                    ({selected.seq.shotCount} shots, ≤ {selected.seq.thresholdMs}ms)
                  </Box> */}

                    {/* <Box sx={{ opacity: 0.9, mt: 0.4 }}>
                    avg {Math.round(selected.seq.avgReactionMs)}ms • min {Math.round(selected.seq.minReactionMs)}ms • max{" "}
                    {Math.round(selected.seq.maxReactionMs)}ms
                  </Box> */}

                    {/* <Box sx={{ opacity: 0.85, mt: 0.4 }}>
                    Players:{" "}
                    {selected.seq.playerIdsInvolved.map((pid) => (playerLabel ? playerLabel(pid) : `P${pid}`)).join(", ")}
                  </Box> */}
                  </Box>
                </Box>
              ) : null
              // <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>No clip selected.</Box>
            }
          </Box>

          {/* BOTTOM: full rally */}

        </Box>
      </Box>
    </Paper>
  );
}
